'use client'

import { Button, Cell, InputText, Spacer, Text } from '@vinted/web-ui'
import { camelCase } from 'lodash'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'

import { getFingerprint } from '@marketplace-web/session-management/audit-util'
import { useDataDomeCaptcha } from '@marketplace-web/bot-detection/data-dome-feature'
import { ErrorItem, ResponseCode } from '@marketplace-web/core-api/api-client-util'
import {
  useBrowserNavigation,
  navigateToPage,
} from '@marketplace-web/browser/browser-navigation-util'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import {
  renderValidation,
  useFormValidationMessage,
} from '@marketplace-web/vendor-abstractions/react-hook-form-feature'
import { normalizedQueryParam, urlWithParams } from '@marketplace-web/browser/url-util'
import {
  changeForgotPassword,
  getForgotPasswordConfirmationCode,
} from '@marketplace-web/security-settings/change-password-data'
import { useFeatureSwitch } from '@marketplace-web/feature-flags/feature-switches-data'

import ChangePasswordRestrictedModal from '../../components/ChangePasswordRestrictedModal'
import { isPasswordChangeRestricted } from '../../utils/errors'
import { ChangeForgotPasswordFormField } from './constants'
import { ChangeForgotPasswordFormModel } from './types'
import { ROOT_URL } from '../../constants/routes'
import { isValueInObject } from '../../utils/object'

const ChangeForgotPassword = () => {
  const {
    setError,
    register,
    handleSubmit,
    formState: { isSubmitting, errors },
  } = useForm<ChangeForgotPasswordFormModel>({
    mode: 'onSubmit',
  })
  const [isInitialized, setIsInitialized] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [secondConfirmationCode, setSecondConfirmationCode] = useState<string | null>(null)
  const [showPasswordChangeRestrictedModal, setShowPasswordChangeRestrictedModal] = useState(false)
  const isChangePasswordRedirectEnabled = useFeatureSwitch('web_change_password_redirect')

  const getErrorMessage = useFormValidationMessage(errors, 'change_forgot_password.fields')
  const redirectUrl = urlWithParams(ROOT_URL, {
    flash_message_code: 'password_changed',
  })

  const { searchParams } = useBrowserNavigation()
  const translate = useTranslate('change_forgot_password')
  const code = normalizedQueryParam(searchParams.code)

  useEffect(() => {
    const fetchSecondConfirmationCode = async () => {
      if (!code) return

      const response = await getForgotPasswordConfirmationCode({ code })

      if ('errors' in response) return

      setSecondConfirmationCode(response.confirmation_code)
    }

    fetchSecondConfirmationCode().then(() => {
      setIsInitialized(true)
    })
  }, [code])

  const setErrors = (validationErrors: Array<ErrorItem>) => {
    validationErrors.forEach(({ field, value }) => {
      const fieldName = camelCase(field)

      if (!isValueInObject(fieldName, ChangeForgotPasswordFormField)) return

      setError(fieldName, { type: 'manual', message: value })
    })
  }

  const togglePasswordChangeRestrictedModal = () => {
    setShowPasswordChangeRestrictedModal(!showPasswordChangeRestrictedModal)
  }

  const handleFormSubmit = async (formData: ChangeForgotPasswordFormModel) => {
    if (!secondConfirmationCode) return

    const response = await changeForgotPassword({
      code: secondConfirmationCode,
      password: formData.password,
      passwordConfirmation: formData.passwordConfirmation,
      fingerprint: await getFingerprint(),
    })

    if (isPasswordChangeRestricted(response)) {
      togglePasswordChangeRestrictedModal()

      return
    }

    if ('errors' in response) {
      if (response.errors.length) {
        setErrors(response.errors)
      }

      if (response.code === ResponseCode.NotFound) {
        setSecondConfirmationCode(null)
      }

      return
    }

    if (isChangePasswordRedirectEnabled) {
      navigateToPage(redirectUrl)

      return
    }

    setIsSuccess(true)
  }

  useDataDomeCaptcha(() => handleSubmit(handleFormSubmit)())

  const renderSubmitButton = () => (
    <Button
      text={translate('actions.submit')}
      type="submit"
      styling="filled"
      isLoading={isSubmitting}
      disabled={isSubmitting}
      testId="change-forgot-password-button"
      onClick={(event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
        if (isSubmitting) {
          event.preventDefault()
        }
      }}
    />
  )

  const renderSuccessMessage = () => (
    <>
      <Spacer size="large" />
      <Cell
        theme="success"
        title={translate('success_message')}
        testId="change-forgot-password-success"
      />
    </>
  )

  const renderForm = () => {
    if (isSuccess) return renderSuccessMessage()

    return (
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <InputText
          {...register(ChangeForgotPasswordFormField.Password, {
            required: true,
          })}
          placeholder={translate('fields.password.title')}
          type="password"
          validation={renderValidation(getErrorMessage(ChangeForgotPasswordFormField.Password))}
          testId="change-forgot-password-enter-new"
        />
        <InputText
          {...register(ChangeForgotPasswordFormField.PasswordConfirmation, {
            required: true,
          })}
          placeholder={translate('fields.password_confirmation.title')}
          type="password"
          validation={renderValidation(
            getErrorMessage(ChangeForgotPasswordFormField.PasswordConfirmation),
          )}
          testId="change-forgot-password-reenter-new"
        />
        {renderSubmitButton()}
      </form>
    )
  }

  if (!isInitialized) {
    return null
  }

  if (!secondConfirmationCode) {
    return (
      <div className="box box--form">
        <Text
          as="h1"
          alignment="center"
          width="parent"
          type="heading"
          text={translate('errors.cant_change_password')}
        />
        <Spacer size="large" />
        <Text
          as="span"
          text={translate('errors.reset_link_expired')}
          testId="change-forgot-password-link-expired"
        />
      </div>
    )
  }

  return (
    <>
      {showPasswordChangeRestrictedModal && (
        <ChangePasswordRestrictedModal onClose={togglePasswordChangeRestrictedModal} />
      )}
      <div className="box box--form">
        <Text as="h1" alignment="center" width="parent" type="heading" text={translate('title')} />
        <Spacer size="large" />
        {renderForm()}
      </div>
    </>
  )
}

export default ChangeForgotPassword
